import { useMemo } from "react";

interface HourlyWaitChartProps {
  airportCode: string;
}

/** Same tier logic as estimateProvider */
const AIRPORT_TIERS: Record<string, "major" | "large" | "medium"> = {
  ATL: "major", LAX: "major", ORD: "major", DFW: "major", DEN: "major",
  JFK: "major", SFO: "major", SEA: "large", LAS: "large", MCO: "large",
  EWR: "major", CLT: "large", PHX: "large", IAH: "large", MIA: "major",
  BOS: "large", MSP: "large", DTW: "large", PHL: "large", LGA: "major",
};

const BASE_WAIT: Record<string, number> = { major: 18, large: 12, medium: 8 };

const HOUR_MULTIPLIERS = [
  0.5, 0.4, 0.3, 0.3, 0.5, 1.3, 1.6, 1.8,
  1.4, 1.0, 0.8, 0.9, 1.0, 1.0, 1.1, 1.3,
  1.5, 1.6, 1.4, 1.0, 0.8, 0.7, 0.6, 0.5,
];

const DAY_MULTIPLIERS = [1.3, 1.2, 0.9, 0.9, 1.0, 1.3, 1.1];

const getBarColor = (minutes: number): string => {
  if (minutes < 10) return "bg-success";
  if (minutes <= 20) return "bg-warning";
  return "bg-destructive";
};

const getBarTextColor = (minutes: number): string => {
  if (minutes < 10) return "text-success";
  if (minutes <= 20) return "text-warning";
  return "text-destructive";
};

export const HourlyWaitChart = ({ airportCode }: HourlyWaitChartProps) => {
  const currentHour = new Date().getHours();
  const currentDay = new Date().getDay();

  const hourlyData = useMemo(() => {
    const tier = AIRPORT_TIERS[airportCode.toUpperCase()] || "medium";
    const base = BASE_WAIT[tier];
    const dayMul = DAY_MULTIPLIERS[currentDay];

    return Array.from({ length: 16 }, (_, i) => {
      const hour = i + 5; // 5 AM to 8 PM
      const wait = Math.round(base * HOUR_MULTIPLIERS[hour] * dayMul);
      return { hour, wait, label: `${hour % 12 || 12}${hour < 12 ? "a" : "p"}` };
    });
  }, [airportCode, currentDay]);

  const maxWait = Math.max(...hourlyData.map((d) => d.wait));
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">
        Wait Times Today — {days[currentDay]}
      </h3>
      <div className="space-y-1.5">
        {hourlyData.map(({ hour, wait, label }) => {
          const isCurrent = hour === currentHour;
          const widthPercent = Math.max(8, (wait / maxWait) * 100);
          const barColor = isCurrent ? "bg-primary" : getBarColor(wait);

          return (
            <div key={hour} className="flex items-center gap-2">
              <span className={`text-xs w-8 text-right tabular-nums ${isCurrent ? "font-bold text-primary" : "text-muted-foreground"}`}>
                {label}
              </span>
              <div className="flex-1 flex items-center gap-2">
                <div
                  className={`h-5 rounded-r-md transition-all duration-300 ${barColor} ${isCurrent ? "ring-2 ring-primary/30" : ""}`}
                  style={{ width: `${widthPercent}%` }}
                />
                <span className={`text-xs tabular-nums font-medium ${isCurrent ? "text-primary font-bold" : getBarTextColor(wait)}`}>
                  {wait}m
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-success" /> Under 10 min</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-warning" /> 10-20 min</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-destructive" /> 20+ min</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-primary" /> Now</span>
      </div>
    </div>
  );
};
